/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, {useState, useEffect} from 'react';
import {Route, Switch, Redirect} from 'react-router';
import { sharedURLs, environment } from "@insticator/insticator-ui";
// redux
import {useSelector, useDispatch, batch} from 'react-redux';
// material
import {Body,  LoadState,  Link, countries, displayMessage, removeMessage, displayModal} from '@insticator/insticator-ui'; // iui-material
import Sidebar from '../../../src/material/structure/sidebar/sidebar';
// divisions
import MonetizationDivision from './monetization/monetization';
import MonetizationDivisionBeta from "./monetization/monetization_beta";
import EngagementDivision from './engagement/engagement';
import CommentingDivision from './commenting/commenting';
import TrendingNowV2Division from './trendingnowv2/trendingnowv2';
import ArticleSentimentV2Division from './articlesentimentv2/articlesentimentv2';

import ModerationDivision from './moderation/moderation';
import TriviaAndPollsDivision from './triviapolls/triviapolls'
import SettingsDivision from "./settings/settings";
import GlobalSettingsDivision from "./globalsettings/globalsettings";
import ProfileDivision from '../profile/profile'

// pages
import OverviewPage from './overview/overview';
import ProductManagerPage from './productmanager/productmanager';
import ReportBuilderPage from "./reportbuilder/reportbuilder";
// styles
import injectSheet from 'react-jss';
import styles from './dashboard_styles';
// utils
// todo get fetchAccountProducts working from the account state slice, reverted to old logic for hotfix
import {fetchAccountProducts, fetchPaymentInformation} from "../../state/thunks/account";
import {fetchAccountProducts as fetchTrending, fetchArticleSentimentAccess} from "../../state/thunks/engagement";
import {fetchDashboardMessage} from "../../state/thunks/settings";
import {setFilter, updateCurrentPage} from "../../state/slices/app";
import { SSP_ACCOUNT_UUIDS } from "./reportbuilder/monetizationform/consts";
import { transpileDataForCountriesInitialFilters } from "../../utils/transpilers/transpileDataForFilters";
import MigrationStatusWidget from './migrationstatuswidget/migrationstatuswidget';
import { Button } from '@insticator/insticator-ui/dist/material';
// migration status
import { fetchMigrationStatus, fetchSitesWithV2Products } from 'state/thunks/migration';
import { setSiteProductsToV1, setSiteProductsToV2 } from 'state/slices/account';


/* ========== ~~~~~~~~~~ DASHBOARD ~~~~~~~~~~ ========== */
const Dashboard = injectSheet(styles)((props) => {
    const dispatch = useDispatch();
    // redux state
    const { siteUUID } = useSelector(state => state.session);
    const sidebarOpen = useSelector(state => state.app.sidebarOpen);
    const mocked = useSelector((state) => state.session.mocked)
    const accountInfoReceived = useSelector((state) => state.account.accountInfoReceived)
    const accountProductsReceived =  useSelector((state) => state.account.accountProductsReceived)
    const approvedAccountProducts = useSelector((state) => state.account.approvedAccountProducts)
    const userUUID = useSelector((state) => state.session.userUUID)
    const userFirstName = useSelector((state) => state.session.userFirstName)
    const userLastName = useSelector((state) => state.session.userLastName)
    const userEmail = useSelector((state) => state.session.userEmail)
    const userPhoneNumber = useSelector((state) => state.session.userPhoneNumber)
    const accountUUID = useSelector((state) => state.session.accountUUID)
    const currentPage = useSelector(state => state.app.currentPage);
    const sitesWithProductsV2 = useSelector(state => state.migrationStatus.sitesWithProductsV2)
    const selectingSite = useSelector(state => state.session.selectingSite)
    // payment info states
    const paymentInfo = useSelector(state => state.account.paymentInfo)
    const messageStatus = useSelector(state => state.messageStatus)
    const permissions = useSelector(state => state.session.permissions)
    // state
    const [isIntercomBooted, setIsIntercomBooted] = useState(false);
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        if (accountInfoReceived && !isIntercomBooted) {
            //Booting up intercom as soon as user is logged in
            window.Intercom("boot", {
                app_id: "oub1vvsv",
                name: mocked ? `MOCKED-${userFirstName} ${userLastName}` : `${userFirstName} ${userLastName}`,
                email: mocked ? `MOCKED-${userEmail}` : userEmail,
                phone: userPhoneNumber,
                user_id: mocked ? `MOCKED-${userUUID}` : userUUID
            });
            setIsIntercomBooted(true)
        }
    }, [accountInfoReceived]);

    // when account changes, set sentiment, account products, and initial filters
    useEffect(() => {
        if (!accountUUID) return;
        dispatch(fetchArticleSentimentAccess(accountUUID))
        dispatch(fetchAccountProducts(accountUUID))
        dispatch(fetchTrending(accountUUID))
        dispatch(setFilter({filter: 'countries', value: []}));
        dispatch(fetchPaymentInformation(accountUUID))
    }, [dispatch, accountUUID])

    useEffect(() => {
      accountProductsLoaded()
    },[accountProductsReceived])

    const accountProductsLoaded = () => {
      if(accountProductsReceived && isLoading) setTimeout(() => setIsLoading(false), 1200)
    }


    // 04.16.25 - https://insticatorinc.atlassian.net/browse/CMMT2-2583
    // Deprecate 1.0 Comment out this migration status 
    // useEffect(() => {
    //   // A lot of UI code assumes that product list has either v2 or v1 commenting,
    //   // but one account effectively can have both at the same time.

    //   // Ideally, it should have been moved to different state piece, but for now, this is a quick fix.
    //   // This code will put COMMENTING_V2 into ACCOUNT products list if site is migrated. Else will put COMMENTING into products list.
    //   // if(!mocked ) return // if not admin, skip this
    //   // Set the Products depending on the site list that has 2.0 products
    //   if(siteUUID && !selectingSite) {
    //     if(sitesWithProductsV2.includes(siteUUID)) {
    //       batch(() => {
    //         dispatch(setSiteProductsToV2())
    //         dispatch(fetchMigrationStatus(siteUUID))
    //       })
    //     } else {
    //       batch(() => {
    //         dispatch(setSiteProductsToV1())
    //         dispatch(fetchMigrationStatus(siteUUID))
    //         if (currentPage === '/dashboard/productmanager') dispatch(updateCurrentPage('/dashboard/overview'))
    //       })
    //     }
    //   }
    // }, [dispatch, selectingSite, siteUUID, sitesWithProductsV2])
  

    const hasPaymentInfoPermission = permissions.includes('settings.paymentInfo') && !mocked

    useEffect(() => {
      const navigateToPaymentInfo = () => dispatch(updateCurrentPage('/dashboard/settings/billing'))
      const hasAnyPaymentInfo = paymentInfo?.paymentInfo?.some(payment => payment.paymentType !== 'NONE')
      const messageAlreadyExists = messageStatus?.incontent?.messages?.some(prop => prop.name === 'nopaymentinfo');

      if(!paymentInfo || hasAnyPaymentInfo) {
        if(messageAlreadyExists) {
          dispatch(removeMessage('name', 'nopaymentinfo', 'incontent', 'messages'))
        }
      } else if(paymentInfo && !hasAnyPaymentInfo && hasPaymentInfoPermission) {
        if(!messageAlreadyExists) {
          dispatch(displayMessage({
            name: 'nopaymentinfo',
            type: 'incontent',
            variant: 'warning',
            icon: `exclamation-triangle`,
            title: null,
            message:
              <span>
                Please add your payment information to receive payments.{' '}
                <Link type={`action`} action={''} operation={'link'} handleClick={navigateToPaymentInfo} text={<strong>Add payment info here.</strong>} color={`primary`} />
              </span>,
            close: false,
            handleClose: null,
            enabled: true,
            pages: ['', '/', 'dashboard', 'dashboard/overview', 'dashboard/monetization',
            'dashboard/trendsandreactions', 'dashboard/commenting', 'dashboard/settings',
            'dashboard/triviapolls', 'dashboard/reportbuilder'],
            pagesExcluded: []
          }))
        }
      }
    },[hasPaymentInfoPermission, paymentInfo])

    useEffect(() => {
        dispatch(fetchDashboardMessage())
    }, [])

    const startDate = useSelector((state) => state.app.filters.dateRange.from)
    const isSspAccount = SSP_ACCOUNT_UUIDS.has(accountUUID)

    useEffect(() => {
        if (!isSspAccount || !startDate) return
        const daysOld = (new Date() - new Date(startDate)) / (1000 * 60 * 60 * 24)
        if (daysOld > 35) {
            dispatch(displayMessage({
                type: 'toaster',
                close: true,
                variant: 'warning',
                time: 5000,
                align: 'top-right',
                icon: 'exclamation-triangle',
                message: 'Site, country, and device filters are not supported for date ranges older than 35 days.'
            }))
        }
    }, [startDate, isSspAccount])

    const showLoading = currentPage.includes('trendsandreactions') || currentPage.includes('productmanager') ? isLoading : !accountProductsReceived || isLoading

    if(showLoading) return <LoadState graphicName={`insticator-icon`} graphicText={`Browser with Search Icon`} bodycopy={`COOL Media is loading your data.`} />;

    return (
        <div className={`dashboard ${props.classes.dashboard} sidebarOpen-${sidebarOpen}`}>
            <Sidebar/>
            <div id={`mainarea`}>
                {/* header goes in pages for flexibility, since some pages utilize different components in the header bar */}
                <Body>
                    <Switch>
                        {/*Routes to subsections*/}
                        <Route exact path='/dashboard' render={() => <Redirect to={'/dashboard/overview'}/>}/>
                        <Route path='/dashboard/productmanager' component={ProductManagerPage} />
                        <Route path='/dashboard/overview' component={OverviewPage}/>
                        <Route path='/dashboard/monetization' component={MonetizationDivisionBeta}/>
                        <Route path='/dashboard/trendsandreactions' component={EngagementDivision}/>
                        <Route path='/dashboard/moderation' component={ModerationDivision} />
                        <Route path='/dashboard/commenting' component={CommentingDivision}/>
                        <Route path='/dashboard/trendingnow' component={TrendingNowV2Division}/>
                        <Route path='/dashboard/articlesentiment' component={ArticleSentimentV2Division}/>
                        <Route path='/dashboard/triviapolls' component={TriviaAndPollsDivision}/>
                        <Route path='/dashboard/reportbuilder' render={ReportBuilderPage}/>
                        <Route path='/profile' component={ProfileDivision}/>
                        <Route path='/dashboard/settings' component={SettingsDivision}/>
                        <Route path='/dashboard/globalSettings' component={GlobalSettingsDivision}/>
                        <Redirect to='/error/404'/>
                    </Switch>
                    {/* <MigrationStatusWidget /> */}
                </Body>
            </div>
            {/*<Footer marginRight={'25px'} />*/}
        </div>
    )
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default Dashboard;
